/**
 * Claude AI Service
 * Handles integration with Anthropic Claude for generating sports betting picks
 */

import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Environment configuration
const CLAUDE_RATE_LIMIT_MS = parseInt(process.env.REACT_APP_CLAUDE_RATE_LIMIT_MS) || 60000; // 1 minute default

// Rate limiting storage key
const RATE_LIMIT_KEY = 'claude_last_request';

/**
 * Check if we can make a Claude API request (rate limiting)
 * @returns {boolean} True if request is allowed
 */
export const canMakeRequest = () => {
  const lastRequest = localStorage.getItem(RATE_LIMIT_KEY);
  if (!lastRequest) return true;

  const timeDiff = Date.now() - parseInt(lastRequest);
  return timeDiff >= CLAUDE_RATE_LIMIT_MS;
};

/**
 * Get remaining time until next request is allowed
 * @returns {number} Milliseconds until next request
 */
export const getRemainingCooldown = () => {
  const lastRequest = localStorage.getItem(RATE_LIMIT_KEY);
  if (!lastRequest) return 0;

  const timeDiff = Date.now() - parseInt(lastRequest);
  const remaining = CLAUDE_RATE_LIMIT_MS - timeDiff;
  return Math.max(0, remaining);
};

/**
 * Log Claude API usage to claudeLogs collection
 * @param {Object} logData - Log data to store
 */
const logClaudeUsage = async (logData) => {
  try {
    await addDoc(collection(db, 'claudeLogs'), {
      ...logData,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error logging Claude usage:', error);
  }
};

/**
 * Generate a sports betting pick using Claude AI
 * @param {Object} oddsData - The odds data to analyze
 * @param {string} userId - User ID making the request
 * @returns {Promise<Object>} Generated pick object
 */
export const generatePick = async (oddsData, userId) => {
  // Check rate limiting
  if (!canMakeRequest()) {
    const remaining = getRemainingCooldown();
    throw new Error(`Rate limit exceeded. Please wait ${Math.ceil(remaining / 1000)} seconds.`);
  }

  const requestStart = Date.now();

  try {
    // Get current user's auth token for authorization
    const { auth } = await import('./firebase');
    const user = auth.currentUser;
    let authToken = null;

    if (user) {
      try {
        authToken = await user.getIdToken();
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
    }

    // Update rate limit timestamp
    localStorage.setItem(RATE_LIMIT_KEY, Date.now().toString());

    // Create the prompt with the pick structure
    const prompt = `You are BettorAI, an expert sports betting analyst. Analyze the following odds data and generate a single betting pick.

ODDS DATA:
${JSON.stringify(oddsData, null, 2)}

Please respond with a JSON object that matches this exact structure:
{
  "gameId": "string - game identifier",
  "sport": "string - sport key (e.g., 'americanfootball_nfl')",
  "homeTeam": "string - home team name",
  "awayTeam": "string - away team name",
  "pickType": "string - type of bet (h2h, spreads, totals)",
  "pickedTeam": "string - team or outcome picked",
  "odds": "number - odds value for the pick",
  "confidence": "string - low, medium, or high",
  "analysis": "string - detailed analysis explaining the pick reasoning",
  "bookmaker": "string - recommended sportsbook for this pick",
  "commenceTime": "string - game start time in ISO format"
}

IMPORTANT: 
- Choose the bet type and outcome that offers the best value based on your analysis
- Confidence should be based on how likely you think this pick will win
- Analysis should be detailed (at least 100 words) explaining your reasoning
- Only respond with valid JSON, no additional text`;

    // Use Netlify function for secure API calls
    // Dynamically determine the function URL based on environment
    let functionUrl;
    if (typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalhost) {
        functionUrl = 'http://localhost:8888/.netlify/functions/claude-ai';
      } else {
        // In production, use the current origin
        functionUrl = `${window.location.origin}/.netlify/functions/claude-ai`;
      }
    } else {
      // Fallback for SSR or other environments
      functionUrl = '/.netlify/functions/claude-ai';
    }

    console.log('🔍 Calling Claude function at:', functionUrl);

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if we have an auth token
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt, userId })
    });

    console.log('🔍 Claude function response status:', response.status);

    if (!response.ok) {
      const error = await response.text();
      console.error('🔥 Claude API request failed:', {
        status: response.status,
        statusText: response.statusText,
        error,
        url: functionUrl
      });
      throw new Error(`API request failed (${response.status}): ${error}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Unknown API error');
    }

    // Validate the generated pick structure
    const pick = result.data;
    const requiredFields = ['gameId', 'sport', 'homeTeam', 'awayTeam', 'pickType', 'pickedTeam', 'odds', 'confidence', 'analysis', 'bookmaker', 'commenceTime'];

    for (const field of requiredFields) {
      if (!pick[field]) {
        throw new Error(`Generated pick missing required field: ${field}`);
      }
    }

    // Validate confidence level
    if (!['low', 'medium', 'high'].includes(pick.confidence.toLowerCase())) {
      pick.confidence = 'medium'; // Default fallback
    }

    const processingTime = Date.now() - requestStart;

    // Log successful generation
    await logClaudeUsage({
      userId,
      action: 'generate_pick',
      success: true,
      processingTimeMs: processingTime,
      pickGenerated: true,
      sport: pick.sport,
      confidence: pick.confidence
    });

    return {
      ...pick,
      aiGenerated: true,
      generatedAt: new Date().toISOString()
    };

  } catch (error) {
    const processingTime = Date.now() - requestStart;

    // Log failed generation
    await logClaudeUsage({
      userId,
      action: 'generate_pick',
      success: false,
      processingTimeMs: processingTime,
      error: error.message,
      pickGenerated: false
    });

    throw error;
  }
};

/**
 * Test Claude API connection — requires a valid Firebase auth token
 * @returns {Promise<boolean>} True if connection successful
 */
export const testConnection = async () => {
  try {
    const { auth } = await import('./firebase');
    const user = auth.currentUser;
    if (!user) throw new Error('Must be signed in to test connection');
    const authToken = await user.getIdToken();

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const functionUrl = isLocalhost
      ? 'http://localhost:8888/.netlify/functions/claude-ai'
      : `${window.location.origin}/.netlify/functions/claude-ai`;

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        prompt: 'Respond with only this exact JSON object and no other text: {"status":"connected"}',
        userId: user.uid,
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Connection test failed (${response.status})`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    const isConnected = result.success && result.data?.status === 'connected';

    if (!isConnected) {
      throw new Error(result.error || 'Unexpected response from Claude API');
    }

    return isConnected;
  } catch (error) {
    console.error('Claude connection test failed:', error);
    throw error;
  }
};